import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useLessons } from '@/composables/useLessons';

const LEARNING_PATH_ROUTE = { name: 'learningPath#setup' };

export function useDefaultQuestionPath() {
  const store = useStore();

  const {
    lessons,
    status,
    getFirstUnansweredFlashcardId,
    getLessons,
  } = useLessons();

  const defaultDashboard = computed(() => {
    return store.getters['dashboards/menuDefaultDashboard'];
  });

  const dashboardWidgetTypes = computed(() => {
    return defaultDashboard.value?.appDashboardsDashboardWidgets
      ?.map(w => w.dashboardWidgetId)
      .filter(id => id != null)
      .map(id => store.getters['dashboardWidgets/widgets'][id])
      .filter(w => w != null)
      .map(w => w.widgetType) || [];
  });

  const hasLessonWidget = computed(() => {
    return dashboardWidgetTypes.value.includes('lesson');
  });

  let lessonsFetched = false;
  watch(hasLessonWidget, (hasWidget) => {
    if (hasWidget && !lessonsFetched) {
      lessonsFetched = true;
      getLessons().catch(() => {});
    }
  }, { immediate: true });

  function resolveLessonRoute() {
    // No lessons yet (a new one is generated on demand) — fall back.
    if (lessons.value.length === 0) {
      return LEARNING_PATH_ROUTE;
    }

    const firstIncompleteLesson = lessons.value.find(lesson => {
      const lessonStatus = status(lesson);
      return lessonStatus === 'not-started' || lessonStatus === 'in-progress';
    });

    // When every lesson is complete, a new one is being generated; keep the
    // user in the lessons flow by routing to the most recent lesson.
    const targetLesson = firstIncompleteLesson || lessons.value[lessons.value.length - 1];

    const flashcardId = getFirstUnansweredFlashcardId(targetLesson)
      || targetLesson.flashcardIds[0];

    return {
      name: 'lessons#show',
      params: {
        lessonId: targetLesson.id,
        flashcardId,
      },
    };
  }

  const defaultQuestionPath = computed(() => {
    // A lesson widget routes to the lesson; otherwise fall back to the learning path.
    if (hasLessonWidget.value) {
      return resolveLessonRoute();
    }

    return LEARNING_PATH_ROUTE;
  });

  return {
    defaultQuestionPath,
  };
}
